<template>
  <v-container fluid>
    <LoadableComponent :loading="loading">
      <v-row>
        <v-col cols="12">
          <v-card outlined>
            <v-card-title>
              <v-icon class="mr-2" color="primary">mdi-truck-trailer</v-icon>
              <span class="subtitle-1 font-weight-medium">{{ $t('trailerIdentifiers') }}</span>
              <v-spacer/>
              <Icon
                icon="mdi-plus"
                tooltipColor="primary"
                :tooltipText="$t('addTrailerIdentifier')"
                :small="false"
                tabindex="0"
                @icon-clicked="addTrailerIdentifierClicked"
                elementId="add-trailer-identifier"
                :disabled="creatingDriverDetail"
              />
            </v-card-title>
            <v-card-text>
              <v-row v-if="trailerIdentifiers.length > 0">
                <v-col v-for="(ti, index) in trailerIdentifiers" :key="`ti-${index}`" cols="12" lg="4">
                  <TrailerIdentifierCard
                    :trailerIdentifier="ti"
                    showCrud
                    @click:edit="editTrailerIdentifier"
                    @click:delete="deleteTrailerIdentifierClicked"
                  />
                </v-col>
              </v-row>
              <v-row v-else>
                <v-col>
                  <NoData>{{ $t('noTrailerIdentifiersAdded') }}</NoData>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card outlined>
            <v-card-title>
              <v-icon class="mr-2" color="primary">mdi-card-account-details</v-icon>
              <span class="subtitle-1 font-weight-medium">{{ $t('driversLicenses') }}</span>
              <v-spacer/>
              <Icon
                icon="mdi-plus"
                tooltipColor="primary"
                :tooltipText="$t('addDriversLicense')"
                :small="false"
                tabindex="0"
                @icon-clicked="addLicenseClicked"
                elementId="add-drivers-license"
                :disabled="creatingDriverDetail"
              />
            </v-card-title>
            <v-card-text>
              <v-row v-if="driverLicenses.length > 0">
                <v-col v-for="(license, index) in driverLicenses" :key="`lic-${index}`" cols="12" lg="4">
                  <DriversLicenseCard
                    :license="license"
                    showCrud
                    @click:edit="editLicense"
                    @click:delete="deleteLicense"
                  />
                </v-col>
              </v-row>
              <v-row v-else>
                <v-col>
                  <NoData>{{ $t('noDriversLicensesAdded') }}</NoData>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </LoadableComponent>

    <v-dialog v-model="licenseFormDialog" width="900px">
      <DriversLicenseForm
        v-if="licenseFormDialog"
        :propLicense="focusedLicense"
        @close="closeLicenseDialog"
        @license-created="licenseCreated"
        @license-updated="licenseUpdated"
      />
    </v-dialog>

    <v-dialog v-model="trailerIdentifierFormDialog" width="600px">
      <TrailerIdentifierForm
        v-if="trailerIdentifierFormDialog"
        :propTrailerIdentifier="focusedTrailerIdentifier"
        @close="closeTrailerIdentifierDialog"
        @trailer-identifier-created="trailerIdentifierCreated"
        @trailer-identifier-updated="trailerIdentifierUpdated"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'AccountDriverDetails',

  components: {
    DriversLicenseCard: () => import('@/components/account/driver-details/DriversLicenseCard.vue'),
    DriversLicenseForm: () => import('@/components/account/driver-details/DriversLicenseForm.vue'),
    TrailerIdentifierCard: () => import('@/components/account/driver-details/TrailerIdentifierCard.vue'),
    TrailerIdentifierForm: () => import('@/components/account/driver-details/TrailerIdentifierForm.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    NoData: () => import('@/components/core/NoData.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue')
  },

  props: {
    accountId: {
      type: Number,
      default: undefined
    },
    driverDetail: {
      type: Object,
      default: undefined
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    licenseFormDialog: false,
    focusedLicense: undefined,
    trailerIdentifierFormDialog: false,
    focusedTrailerIdentifier: undefined,
    creatingDriverDetail: false,
    localDriverDetailId: undefined
  }),

  computed: {
    hasDriverDetail () {
      return !!this.driverDetailId
    },

    driverDetailId () {
      return this.driverDetail?.accountDriverDetailId || this.localDriverDetailId
    },

    driverLicenses () {
      return this.driverDetail?.driversLicenses || []
    },

    trailerIdentifiers () {
      return this.driverDetail?.trailerIdentifiers || []
    }
  },

  watch: {
    licenseFormDialog (val) {
      if (!val) {
        this.focusedLicense = undefined
      }
    },

    trailerIdentifierFormDialog (val) {
      if (!val) {
        this.focusedTrailerIdentifier = undefined
      }
    },

    'driverDetail.accountDriverDetailId': {
      handler (val) {
        if (val) {
          this.localDriverDetailId = undefined
        }
      },
      immediate: true
    }
  },

  methods: {
    ...mapActions('account-driver-details', [
      'createDriverDetail',
      'updateDriverDetail',
      'createDriversLicense',
      'updateDriversLicense',
      'deleteDriversLicense',
      'createTrailerIdentifier',
      'updateTrailerIdentifier',
      'deleteTrailerIdentifier'
    ]),

    async ensureDriverDetail () {
      if (this.hasDriverDetail) return

      if (!this.accountId) {
        this.localDriverDetailId = -1
        this.$emit('driver-detail-created', { driversLicenses: [], trailerIdentifiers: [] })
        return
      }

      this.creatingDriverDetail = true
      try {
        const response = await this.createDriverDetail({
          accountId: this.accountId,
          driverDetail: { accountId: this.accountId, driversLicenses: [], trailerIdentifiers: [] }
        })
        this.localDriverDetailId = response.accountDriverDetailId
      } finally {
        this.creatingDriverDetail = false
      }
    },

    // License methods
    async addLicenseClicked () {
      await this.ensureDriverDetail()
      this.openLicenseDialog()
    },

    openLicenseDialog () {
      this.licenseFormDialog = true
    },

    closeLicenseDialog () {
      this.licenseFormDialog = false
      this.focusedLicense = undefined
    },

    editLicense (license) {
      this.focusedLicense = license
      this.licenseFormDialog = true
    },

    async licenseCreated (license) {
      if (!this.accountId || !this.driverDetailId) {
        this.$emit('license-created', license)
        this.closeLicenseDialog()
        return
      }

      await this.createDriversLicense({
        accountId: this.accountId,
        driverDetailId: this.driverDetailId,
        license
      })
      this.$emit('license-created', license)
      this.closeLicenseDialog()
    },

    async licenseUpdated (license) {
      if (!this.accountId || !this.driverDetailId || !license.driversLicenseId) {
        this.$emit('license-updated', { license, index: this.findLicenseIndex(this.focusedLicense) })
        this.closeLicenseDialog()
        return
      }

      await this.updateDriversLicense({
        accountId: this.accountId,
        driverDetailId: this.driverDetailId,
        licenseId: license.driversLicenseId,
        modifiedAt: license.modifiedAt,
        license
      })
      this.$emit('license-updated', { license, index: this.findLicenseIndex(this.focusedLicense) })
      this.closeLicenseDialog()
    },

    async deleteLicense (license) {
      if (!this.accountId || !this.driverDetailId || !license.driversLicenseId) {
        this.$emit('license-deleted', { license, index: this.findLicenseIndex(license) })
        return
      }

      await this.deleteDriversLicense({
        accountId: this.accountId,
        driverDetailId: this.driverDetailId,
        licenseId: license.driversLicenseId
      })

      this.$emit('license-deleted', { license, index: this.findLicenseIndex(license) })
    },

    findLicenseIndex (license) {
      return this.driverLicenses.findIndex(l =>
        l.licenseNumber === license.licenseNumber &&
        l.firstName === license.firstName &&
        l.lastName === license.lastName
      )
    },

    // Trailer identifier methods
    async addTrailerIdentifierClicked () {
      await this.ensureDriverDetail()
      this.openTrailerIdentifierDialog()
    },

    openTrailerIdentifierDialog () {
      this.trailerIdentifierFormDialog = true
    },

    closeTrailerIdentifierDialog () {
      this.trailerIdentifierFormDialog = false
      this.focusedTrailerIdentifier = undefined
    },

    editTrailerIdentifier (trailerIdentifier) {
      this.focusedTrailerIdentifier = trailerIdentifier
      this.trailerIdentifierFormDialog = true
    },

    async trailerIdentifierCreated (trailerIdentifier) {
      if (!this.accountId || !this.driverDetailId) {
        this.$emit('trailer-identifier-created', trailerIdentifier)
        this.closeTrailerIdentifierDialog()
        return
      }

      await this.createTrailerIdentifier({
        accountId: this.accountId,
        driverDetailId: this.driverDetailId,
        trailerIdentifier
      })
      this.$emit('trailer-identifier-created', trailerIdentifier)
      this.closeTrailerIdentifierDialog()
    },

    async trailerIdentifierUpdated (trailerIdentifier) {
      if (!this.accountId || !this.driverDetailId || !trailerIdentifier.trailerIdentifierId) {
        this.$emit('trailer-identifier-updated', { trailerIdentifier, index: this.findTrailerIdentifierIndex(this.focusedTrailerIdentifier) })
        this.closeTrailerIdentifierDialog()
        return
      }

      await this.updateTrailerIdentifier({
        accountId: this.accountId,
        driverDetailId: this.driverDetailId,
        trailerIdentifierId: trailerIdentifier.trailerIdentifierId,
        trailerIdentifier
      })
      this.$emit('trailer-identifier-updated', { trailerIdentifier, index: this.findTrailerIdentifierIndex(this.focusedTrailerIdentifier) })
      this.closeTrailerIdentifierDialog()
    },

    async deleteTrailerIdentifierClicked (trailerIdentifier) {
      if (!this.accountId || !this.driverDetailId || !trailerIdentifier.trailerIdentifierId) {
        this.$emit('trailer-identifier-deleted', { trailerIdentifier, index: this.findTrailerIdentifierIndex(trailerIdentifier) })
        return
      }

      await this.deleteTrailerIdentifier({
        accountId: this.accountId,
        driverDetailId: this.driverDetailId,
        trailerIdentifierId: trailerIdentifier.trailerIdentifierId
      })

      this.$emit('trailer-identifier-deleted', { trailerIdentifier, index: this.findTrailerIdentifierIndex(trailerIdentifier) })
    },

    findTrailerIdentifierIndex (trailerIdentifier) {
      return this.trailerIdentifiers.findIndex(ti =>
        ti.value === trailerIdentifier.value
      )
    }
  }
}
</script>
